import React, { Component } from "react";
import {
  Row, Col, Card, Tag, Button, Divider, Empty,
  notification, Spin, Tabs, Avatar, Tooltip,
} from "antd";
import {
  UserOutlined, CheckCircleOutlined, ClockCircleOutlined,
  MailOutlined, ReloadOutlined, UserAddOutlined,
} from "@ant-design/icons";
import { noAuthlist, add } from "../api/Apis";
import { withAppContext } from "../store/contextStore";
import Invitation from "./Invitation";
import ContactManager from "./ContactManager";

const { TabPane } = Tabs;

const NAVY   = "#1A2280";
const WHITE  = "#FFFFFF";
const GOLD   = "#D4921A";
const GREEN  = "#38A169";
const BORDER = "#E2E8F0";
const MUTED  = "#718096";

const PROFILE_LABELS = {
  AG: { label: "Agent",        color: "blue"    },
  L:  { label: "Lender",      color: "green"   },
  V:  { label: "Vendor",      color: "purple"  },
  B:  { label: "Buyer",       color: "orange"  },
  S:  { label: "Seller",      color: "gold"    },
  HO: { label: "Homeowner",   color: "cyan"    },
  SB: { label: "Buyer/Seller", color: "orange" },
};

const PLAN_LABELS = {
  free:         { label: "Free",         color: "default" },
  basic:        { label: "Basic",        color: "blue"    },
  member:       { label: "Member",       color: "blue"    },
  advanced:     { label: "Advanced",     color: "green"   },
  professional: { label: "Professional", color: "purple"  },
  lifetime:     { label: "Lifetime",     color: "gold"    },
  agent:        { label: "Agent",        color: "green"   },
  team_leader:  { label: "Team Leader",  color: "red"     },
};

class MyNetwork extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading:          true,
      allUsers:         [],
      invitedAgents:    [],
      invitedLenders:   [],
      invitedVendors:   [],
      invitedConsumers: [],
      inviteLinkCopied: false,
      reinviting:       {},
      showInviteModal:  false,
      inviteType:       "agent",
    };
  }

  componentDidMount() { this.loadNetwork(); }

  // Helper: check if a user was referred by the current agent using ANY field variant
  isReferredByMe = (u, myId) => {
    if (!u || !myId) return false;
    return (
      (u.referedById      && String(u.referedById)      === String(myId)) ||
      (u.referredBy       && String(u.referredBy)       === String(myId)) ||
      (u.invitedBy        && String(u.invitedBy)        === String(myId)) ||
      (u.referredByAgent  && String(u.referredByAgent)  === String(myId)) ||
      (u.agentReferral    && String(u.agentReferral)    === String(myId))
    );
  };

  loadNetwork = async () => {
    const { userDetails } = this.props.context.state;
    if (!userDetails?.email) return;
    this.setState({ loading: true });

    try {
      const inviteHistory = userDetails.invitationHistory || [];

      const result = await noAuthlist("getUsers");
      const allUsers = result.success ? (result.data || []) : [];

      // Build email → user map and referred-by-me map
      const signedUpByEmail = {};
      const signedUpByRef   = {};  // users referred by me (any field variant)

      // Get all possible ID variants for the current user
      // context.state.userDetails might have id, userid, userId, or sub
      const myIds = new Set([
        userDetails.id,
        userDetails.userid,
        userDetails.userId,
        userDetails.sub,
      ].filter(Boolean).map(String));

      console.log("[MyNetwork] checking referrals for IDs:", [...myIds]);

      allUsers.forEach(u => {
        if (u.email) signedUpByEmail[u.email.toLowerCase()] = u;
        // Check against ALL possible id variants
        const isReferred = [...myIds].some(myId => this.isReferredByMe(u, myId));
        if (isReferred) {
          signedUpByRef[u.email?.toLowerCase()] = u;
          console.log("[MyNetwork] found referred user:", u.firstName, u.lastName, u.userProfile);
        }
      });

      // Enrich invite history entries
      const enriched = inviteHistory.map(inv => {
        const key = inv.email?.toLowerCase();
        const signedUpUser = signedUpByEmail[key] || signedUpByRef[key] || null;
        return {
          email:        inv.email,
          firstName:    signedUpUser?.firstName || inv.firstName || inv.email?.split("@")[0] || "",
          lastName:     signedUpUser?.lastName  || inv.lastName  || "",
          // Use actual profile from DB if signed up, fallback to invite type
          userProfile:  signedUpUser?.userProfile || inv.type || "AG",
          sentAt:       inv.sentAt,
          pending:      !signedUpUser,
          id:           signedUpUser?.id               || null,
          plan:         signedUpUser?.plan             || signedUpUser?.planTier || "free",
          profilePicture: signedUpUser?.profilePicture || null,
          subscriptionPack: signedUpUser?.subscriptionPack || null,
          signedUpAt:   signedUpUser?.createdAt        || null,
          confirmedReferral: !!(signedUpUser && [...myIds].some(mid => this.isReferredByMe(signedUpUser, mid))),
        };
      });

      // Add organic signups (referred via link, not in inviteHistory)
      const enrichedEmails = new Set(enriched.map(e => e.email?.toLowerCase()));
      // Re-scan for any missed users using localStorage as fallback id source
      const lsUser = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const lsIds = new Set([lsUser.id, lsUser.userid, lsUser.userId].filter(Boolean).map(String));
      // Merge any additional ids from localStorage
      [...lsIds].forEach(id => myIds.add(id));

      // Second pass for any users missed in first scan
      allUsers.forEach(u => {
        const key = u.email?.toLowerCase();
        if (!signedUpByRef[key]) {
          const isReferred = [...myIds].some(myId => this.isReferredByMe(u, myId));
          if (isReferred) {
            signedUpByRef[key] = u;
            console.log("[MyNetwork] second pass found:", u.firstName, u.lastName);
          }
        }
      });

      Object.values(signedUpByRef).forEach(u => {
        const emailKey = u.email?.toLowerCase();
        // If already in enriched but with wrong userProfile, fix it
        const existingIdx = enriched.findIndex(e => e.email?.toLowerCase() === emailKey);
        if (existingIdx >= 0) {
          const existing = enriched[existingIdx];
          // Update with real DB data if userProfile was defaulted incorrectly
          if (!existing.userProfile || existing.userProfile === "AG" && u.userProfile !== "AG") {
            enriched[existingIdx] = { ...existing, userProfile: u.userProfile || existing.userProfile, firstName: u.firstName || existing.firstName, lastName: u.lastName || existing.lastName };
            console.log("[MyNetwork] corrected userProfile for:", u.firstName, "from default to", u.userProfile);
          }
          return; // already in list, skip push
        }
        if (!enrichedEmails.has(emailKey)) {
          enriched.push({
            email:       u.email,
            firstName:   u.firstName || "",
            lastName:    u.lastName  || "",
            userProfile: u.userProfile || "B",
            sentAt:      null,
            pending:     false,
            id:          u.id,
            plan:        u.plan || u.planTier || "free",
            profilePicture: u.profilePicture || null,
            subscriptionPack: u.subscriptionPack || null,
            signedUpAt:  u.createdAt || null,
            confirmedReferral: true,
            organicSignup: true,
          });
        }
      });

      enriched.sort((a, b) => {
        if (a.confirmedReferral && !b.confirmedReferral) return -1;
        if (!a.confirmedReferral && b.confirmedReferral) return 1;
        if (!a.pending && b.pending) return -1;
        if (a.pending && !b.pending) return 1;
        return new Date(b.sentAt || 0) - new Date(a.sentAt || 0);
      });

      this.setState({
        allUsers,
        invitedAgents:    enriched.filter(u => u.userProfile === "AG"),
        invitedLenders:   enriched.filter(u => u.userProfile === "L"),
        invitedVendors:   enriched.filter(u => u.userProfile === "V"),
        invitedConsumers: enriched.filter(u => ["B", "S", "HO", "SB"].includes(u.userProfile)),
        loading: false,
      });
    } catch(e) {
      console.error("MyNetwork load error:", e);
      this.setState({ loading: false });
    }
  };

  handleReinvite = async (invitee) => {
    const { userDetails } = this.props.context.state;
    const key = invitee.email;
    this.setState(p => ({ reinviting: { ...p.reinviting, [key]: true } }));
    try {
      const result = await add("sendInvitation", {
        entityData: {
          firstName:  invitee.firstName || invitee.email,
          lastName:   invitee.lastName  || "",
          email:      invitee.email,
          agentName:  `${userDetails.firstName} ${userDetails.lastName}`.trim(),
          agentId:    userDetails.id,
          typeOfUser: invitee.userProfile === "AG" ? "agent"
                    : invitee.userProfile === "L"  ? "lender"
                    : invitee.userProfile === "V"  ? "vendor"
                    : "agent",
        },
      });
      if (result.success) {
        notification.success({ message: "Invite sent!", description: `Re-invite sent to ${invitee.email}` });
      } else throw new Error(result.errors || "Failed");
    } catch(e) {
      notification.error({ message: "Failed to send invite", description: e.message });
    }
    this.setState(p => ({ reinviting: { ...p.reinviting, [key]: false } }));
  };

  renderUserCard = (user) => {
    const profileType = PROFILE_LABELS[user.userProfile] || { label: user.userProfile, color: "default" };
    const planType    = PLAN_LABELS[user.plan] || PLAN_LABELS[user.planTier] || { label: "Free", color: "default" };
    const hasPicture  = user.profilePicture?.[0];
    const photoUrl    = hasPicture
      ? `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/public/${user.profilePicture[0]}`
      : null;
    const isPending  = user.pending;
    const reinviting = this.state.reinviting[user.email];

    return (
      <Card key={user.email}
        style={{ marginBottom: "12px", borderRadius: "12px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: "14px 18px" }}>
        <Row align="middle" gutter={[12, 0]}>
          <Col style={{ flexShrink: 0 }}>
            <Avatar src={photoUrl} size={48} icon={<UserOutlined />}
              style={{ background: isPending ? "#f0f0f0" : NAVY, color: "white" }}>
              {!photoUrl && user.firstName?.charAt(0)?.toUpperCase()}
            </Avatar>
          </Col>
          <Col flex="1">
            <Row align="middle" style={{ gap: "8px", marginBottom: "2px" }}>
              <span style={{ fontWeight: 700, fontSize: "14px", color: NAVY }}>
                {isPending
                  ? user.email
                  : `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}
              </span>
              <Tag color={profileType.color} style={{ margin: 0, borderRadius: "20px", fontSize: "11px" }}>
                {profileType.label}
              </Tag>
              {!isPending && (
                <Tag color={planType.color} style={{ margin: 0, borderRadius: "20px", fontSize: "11px" }}>
                  {planType.label}
                </Tag>
              )}
            </Row>
            <Row align="middle" style={{ gap: "8px" }}>
              {isPending ? (
                <>
                  <ClockCircleOutlined style={{ color: GOLD, fontSize: "12px" }} />
                  <span style={{ fontSize: "12px", color: MUTED }}>
                    Invite sent {user.sentAt ? new Date(user.sentAt).toLocaleDateString() : ""} — awaiting signup
                  </span>
                </>
              ) : (
                <>
                  <CheckCircleOutlined style={{ color: GREEN, fontSize: "12px" }} />
                  <span style={{ fontSize: "12px", color: GREEN, fontWeight: 600 }}>Account created</span>
                  {user.confirmedReferral && (
                    <Tag style={{ margin: 0, borderRadius: "20px", fontSize: "10px", background: "#fffbf0", border: `1px solid ${GOLD}`, color: GOLD, fontWeight: 700 }}>
                      ✓ Confirmed Referral
                    </Tag>
                  )}
                  {user.organicSignup && (
                    <Tag style={{ margin: 0, borderRadius: "20px", fontSize: "10px", background: "#f0f7f0", border: "1px solid #7A8F6E", color: "#7A8F6E", fontWeight: 600 }}>
                      Organic Signup
                    </Tag>
                  )}
                  {user.email && (
                    <span style={{ fontSize: "12px", color: MUTED, marginLeft: "8px" }}>
                      <MailOutlined style={{ marginRight: "3px" }} />{user.email}
                    </span>
                  )}
                  {user.signedUpAt && (
                    <span style={{ fontSize: "11px", color: MUTED, marginLeft: "8px" }}>
                      Joined {new Date(user.signedUpAt).toLocaleDateString()}
                    </span>
                  )}
                </>
              )}
            </Row>
          </Col>
          <Col style={{ flexShrink: 0 }}>
            <Tooltip title={isPending ? "Send invite again" : "Send another invite"}>
              <Button size="small" icon={<ReloadOutlined />} loading={reinviting}
                onClick={() => this.handleReinvite(user)}
                style={{ borderRadius: "50px", borderColor: NAVY, color: NAVY, fontSize: "12px" }}>
                {isPending ? "Resend" : "Invite Again"}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </Card>
    );
  };

  renderSection = (users, type) => {
    const { userDetails } = this.props.context.state;
    const inviterProfile = userDetails?.userProfile || "AG";
    const canInvite = ["AG", "L", "V"].includes(inviterProfile);

    const typeLabels = {
      AG: { plural: "Agents",  inviteLabel: "Invite Agent",  inviteTitle: inviterProfile === "L" ? "lenderInvitesAgent" : inviterProfile === "V" ? "vendorInvitesPro" : "agent" },
      L:  { plural: "Lenders", inviteLabel: "Invite Lender", inviteTitle: inviterProfile === "L" ? "lender" : "Lendor" },
      V:  { plural: "Vendors", inviteLabel: "Invite Vendor", inviteTitle: "vendor" },
    };
    const { plural, inviteLabel } = typeLabels[type] || { plural: type, inviteLabel: "Invite" };

    return (
      <div>
        <Row align="middle" justify="space-between" style={{ marginBottom: "16px" }}>
          <div style={{ fontSize: "15px", fontWeight: 700, color: NAVY }}>
            {users.length} {plural} in Your Network
          </div>
          {canInvite && (
            <Button type="primary" icon={<UserAddOutlined />}
              onClick={() => this.setState({ showInviteModal: true, inviteType: type })}
              style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY, fontSize: "13px" }}>
              {inviteLabel}
            </Button>
          )}
        </Row>
        {users.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ color: MUTED }}>No {plural.toLowerCase()} in your network yet.{canInvite && " Use the button above to invite some!"}</span>} />
        ) : users.map(u => this.renderUserCard(u))}
      </div>
    );
  };

  // ── Consumers section with Buyer / Seller / Homeowner sub-tabs ──────────────
  renderConsumersSection = (users) => {
    const buyers     = users.filter(u => u.userProfile === "B" || u.userProfile === "SB");
    const sellers    = users.filter(u => u.userProfile === "S");
    const homeowners = users.filter(u => u.userProfile === "HO");
    const { userDetails } = this.props.context.state;
    const canInvite  = ["AG", "L", "V"].includes(userDetails?.userProfile);

    return (
      <div>
        <Row align="middle" justify="space-between" style={{ marginBottom: "16px" }}>
          <div style={{ fontSize: "15px", fontWeight: 700, color: NAVY }}>
            {users.length} Clients in Your Network
          </div>
          {canInvite && (
            <Button type="primary" icon={<UserAddOutlined />}
              onClick={() => this.setState({ showInviteModal: true, inviteType: "B" })}
              style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY, fontSize: "13px" }}>
              Invite Client
            </Button>
          )}
        </Row>

        <Tabs size="small" tabBarStyle={{ marginBottom: "12px" }}>
          <TabPane tab={<span>🏠 Buyers ({buyers.length})</span>} key="buyers">
            {buyers.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: MUTED }}>No buyers in your network yet.</span>} />
              : buyers.map(u => this.renderUserCard(u))}
          </TabPane>
          <TabPane tab={<span>🏷 Sellers ({sellers.length})</span>} key="sellers">
            {sellers.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: MUTED }}>No sellers in your network yet.</span>} />
              : sellers.map(u => this.renderUserCard(u))}
          </TabPane>
          <TabPane tab={<span>🏡 Homeowners ({homeowners.length})</span>} key="homeowners">
            {homeowners.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: MUTED }}>No homeowners in your network yet.</span>} />
              : homeowners.map(u => this.renderUserCard(u))}
          </TabPane>
          <TabPane tab={<span>All ({users.length})</span>} key="all">
            {users.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: MUTED }}>No clients yet.</span>} />
              : users.map(u => this.renderUserCard(u))}
          </TabPane>
        </Tabs>
      </div>
    );
  };

  render() {
    const { loading, invitedAgents, invitedLenders, invitedVendors, invitedConsumers,
      inviteLinkCopied, showInviteModal, inviteType } = this.state;
    const { userDetails } = this.props.context.state;
    const inviterProfile = userDetails?.userProfile || "AG";
    const totalNetwork = invitedAgents.length + invitedLenders.length + invitedVendors.length + invitedConsumers.length;

    return (
      <div style={{ padding: "16px 0" }}>
        {/* Network growth headline */}
        <div style={{ background: "linear-gradient(135deg,#0F1560,#1A2280)", borderRadius: "14px", padding: "20px 24px", marginBottom: "20px", borderLeft: "4px solid #D4921A" }}>
          <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "15px", marginBottom: "8px" }}>
            🌱 Build Your Network. Earn More Together.
          </div>
          <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "13px", lineHeight: 1.7 }}>
            Use your personal invitation links to build and track your network. The more you grow your network of professionals, the more incentives you will earn — including potential future stock benefits, free upgrades, revenue share, and more. Let's grow this together!
          </div>
        </div>

        {/* Summary bar */}
        <Row gutter={[12, 12]} style={{ marginBottom: "20px" }}>
          {[
            { label: "Total Network", value: totalNetwork,             color: NAVY },
            { label: "Agents",        value: invitedAgents.length,    color: "#1890ff" },
            { label: "Lenders",       value: invitedLenders.length,   color: GREEN },
            { label: "Vendors",       value: invitedVendors.length,   color: "#722ed1" },
            { label: "Buyers",        value: invitedConsumers.filter(u => u.userProfile === "B" || u.userProfile === "SB").length, color: GOLD },
            { label: "Sellers",       value: invitedConsumers.filter(u => u.userProfile === "S").length,  color: "#D4921A" },
            { label: "Homeowners",    value: invitedConsumers.filter(u => u.userProfile === "HO").length, color: "#1A5C40" },
          ].map(({ label, value, color }) => (
            <Col key={label} xs={12} sm={8} md={6} lg={3}>
              <Card style={{ borderRadius: "12px", border: `1px solid ${BORDER}`, textAlign: "center" }} bodyStyle={{ padding: "12px 8px" }}>
                <div style={{ fontSize: "22px", fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: "11px", color: MUTED, marginTop: "2px" }}>{label}</div>
              </Card>
            </Col>
          ))}
        </Row>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <div style={{ color: MUTED, marginTop: "12px" }}>Loading your network...</div>
          </div>
        ) : (
          <Tabs defaultActiveKey={inviterProfile === "V" ? "V" : "AG"} tabBarStyle={{ fontWeight: 600, color: NAVY }}>
            <TabPane tab={<span>👤 Agents ({invitedAgents.length})</span>} key="AG">
              {this.renderSection(invitedAgents, "AG")}
            </TabPane>
            <TabPane tab={<span>🏦 Lenders ({invitedLenders.length})</span>} key="L">
              {this.renderSection(invitedLenders, "L")}
            </TabPane>
            <TabPane tab={<span>🔧 Vendors ({invitedVendors.length})</span>} key="V">
              {this.renderSection(invitedVendors, "V")}
            </TabPane>
            <TabPane tab={<span>🏡 Clients ({invitedConsumers.length})</span>} key="consumers">
              {this.renderConsumersSection(invitedConsumers)}
            </TabPane>
            <TabPane tab={<span>📋 Contacts</span>} key="contacts">
            <ContactManager />
            </TabPane>
            <TabPane tab={<span>🔗 Invite Link</span>} key="invite_link">
              <div style={{ padding: "8px 0" }}>
                <div style={{ background: "linear-gradient(135deg,#0F1560,#1A2280)", borderRadius: "14px", padding: "24px", marginBottom: "16px" }}>
                  <div style={{ color: WHITE, fontWeight: 800, fontSize: "16px", marginBottom: "6px" }}>🔗 Your Personal Invite Links</div>
                  <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "13px", marginBottom: "18px", lineHeight: 1.5 }}>
                    Share these links anywhere. Anyone who signs up is automatically linked to you.
                  </div>
                  {[
                    { id: "mnClientLink", type: "B",  label: "🏡 For Clients & Consumers", color: "#7EC8E3" },
                    { id: "mnAgentLink",  type: "AG", label: "🤝 For Fellow Agents",       color: "#A5B4FC" },
                    { id: "mnLenderLink", type: "L",  label: "🏦 For Lenders",             color: "#86EFAC" },
                    { id: "mnVendorLink", type: "V",  label: "🔧 For Vendors",             color: "#FCD34D" },
                  ].map(({ id, type, label, color }) => (
                    <div key={id} style={{ marginBottom: "16px" }}>
                      <div style={{ color, fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>{label}</div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <input readOnly id={id}
                          value={`https://www.myhomebuddha.com/register?ref=${userDetails?.id || ""}&type=${type}&agentName=${encodeURIComponent((userDetails?.firstName || "") + " " + (userDetails?.lastName || ""))}`}
                          style={{ flex: 1, padding: "9px 12px", borderRadius: "8px", border: "none", fontSize: "12px", background: "rgba(255,255,255,0.92)", color: "#111", minWidth: 0 }} />
                        <Button size="small" type="primary"
                          style={{ background: GOLD, border: "none", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}
                          onClick={() => {
                            document.getElementById(id).select();
                            document.execCommand("copy");
                            this.setState({ inviteLinkCopied: id });
                            setTimeout(() => this.setState({ inviteLinkCopied: false }), 2500);
                          }}>
                          {inviteLinkCopied === id ? "✓ Copied!" : "📋 Copy"}
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div style={{ color: "rgba(255,255,255,0.38)", fontSize: "10px", marginTop: "4px" }}>Signups via any link appear in the correct network tab automatically</div>
                </div>

                {/* Email invite buttons */}
                <div style={{ background: "white", borderRadius: "12px", border: `1px solid ${BORDER}`, padding: "20px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: NAVY, marginBottom: "4px" }}>✉️ Invite by Email</div>
                  <div style={{ fontSize: "12px", color: MUTED, marginBottom: "14px" }}>Send a personalised invitation email directly.</div>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {[
                      { type: "AG", label: "Invite Agent",   icon: "👤" },
                      { type: "L",  label: "Invite Lender",  icon: "🏦" },
                      { type: "V",  label: "Invite Vendor",  icon: "🔧" },
                      { type: "B",  label: "Invite Client",  icon: "🏡" },
                    ].map(({ type, label, icon }) => (
                      <Button key={type} icon={<MailOutlined />}
                        onClick={() => this.setState({ showInviteModal: true, inviteType: type })}
                        style={{ borderRadius: "50px", borderColor: NAVY, color: NAVY, fontWeight: 600 }}>
                        {icon} {label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </TabPane>
          </Tabs>
        )}

        {showInviteModal && (
          <Invitation
            enableInvitationModal={showInviteModal}
            handleCloseInvitationModal={() => { this.setState({ showInviteModal: false }); this.loadNetwork(); }}
            invitationTitle={
              inviteType === "AG" ? (inviterProfile === "L" ? "lenderInvitesAgent" : inviterProfile === "V" ? "vendorInvitesPro" : "agent") :
              inviteType === "L"  ? (inviterProfile === "L" ? "lender" : "Lendor") :
              "vendor"
            }
          />
        )}
      </div>
    );
  }
}

export default withAppContext(MyNetwork);